/**
 * File: useUiState.ts
 * Path: apps/admin/src/modules/app/hooks/useUiState.ts
 * Description: Global UI state management for sidebar toggles and theme preferences.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** Headings that were renamed: a closed old heading stays closed. */
const RENAMED_NAV_GROUPS: Record<string, string> = { Sales: "CRM" };

/** Saved state from before a heading was renamed, with the new names. */
export const migrateUIState = (persisted: unknown): unknown => {
  if (typeof persisted !== "object" || persisted === null) return persisted;
  const state = persisted as { closedNavGroups?: unknown };
  if (!Array.isArray(state.closedNavGroups)) return persisted;
  const renamed = state.closedNavGroups.map((label: unknown) =>
    typeof label === "string" ? (RENAMED_NAV_GROUPS[label] ?? label) : label,
  );
  return { ...state, closedNavGroups: [...new Set(renamed)] };
};

interface UIState {
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
  setSidebarCollapsed: (state: boolean) => void;
  /** Sidebar headings (CRM, CMS, ...) the user has closed. */
  closedNavGroups: string[];
  toggleNavGroup: (label: string) => void;
  openNavGroup: (label: string) => void;
}

/**
 * useUIStore
 * Persists the sidebar state to localStorage so the user's preference 
 * remains consistent across page refreshes.
 */
export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      isSidebarCollapsed: false,
      toggleSidebar: () => set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),
      setSidebarCollapsed: (state: boolean) => set({ isSidebarCollapsed: state }),
      closedNavGroups: [],
      toggleNavGroup: (label: string) =>
        set((state) => ({
          closedNavGroups: state.closedNavGroups.includes(label)
            ? state.closedNavGroups.filter((group) => group !== label)
            : [...state.closedNavGroups, label],
        })),
      openNavGroup: (label: string) =>
        set((state) => ({
          closedNavGroups: state.closedNavGroups.filter((group) => group !== label),
        })),
    }),
    {
      name: 'idk-admin-ui-storage',
      // 1: the Sales heading became CRM.
      version: 1,
      migrate: (persisted) => migrateUIState(persisted) as UIState,
    }
  )
);